import type { AssetsSTC } from '~/types/capabilities'
import type { Schedule, ScheduleFeatures } from '~/types/schedule'

export const DEFAULT_SCHEDULE_FEATURES: ScheduleFeatures = {
  supportsSolarTrigger: true,
  supportsRecurrence: true,
  supportsAssetEditingAfterPublication: true,
  supportsTargetType: ['asset', 'group', 'route', 'zone'],
  isLegacySchedule: true
}

function withLegacyDefault(features: ScheduleFeatures): ScheduleFeatures {
  return { ...features, isLegacySchedule: features.isLegacySchedule ?? true }
}

export function resolveScheduleFeatures(schedule: Schedule | undefined): ScheduleFeatures {
  const features = schedule?.scheduleFeatures
  if (!features) {
    return DEFAULT_SCHEDULE_FEATURES
  }
  const keyed = (schedule?.assetTypeIds ?? [])
    .map((assetTypeId) => features[assetTypeId])
    .filter((entry): entry is ScheduleFeatures => Boolean(entry))
    .map(withLegacyDefault)
  if (keyed.length === 0) {
    const fallback = Object.values(features)[0]
    return fallback ? withLegacyDefault(fallback) : DEFAULT_SCHEDULE_FEATURES
  }
  return keyed.reduce(intersectScheduleFeatures)
}

function intersectScheduleFeatures(a: ScheduleFeatures, b: ScheduleFeatures): ScheduleFeatures {
  return {
    supportsSolarTrigger: a.supportsSolarTrigger && b.supportsSolarTrigger,
    supportsRecurrence: a.supportsRecurrence && b.supportsRecurrence,
    supportsAssetEditingAfterPublication:
      a.supportsAssetEditingAfterPublication && b.supportsAssetEditingAfterPublication,
    supportsTargetType: a.supportsTargetType.filter((target) => b.supportsTargetType.includes(target)),
    isLegacySchedule: Boolean(a.isLegacySchedule || b.isLegacySchedule)
  }
}

export function resolveAssetTypeScheduleFeatures(stcs: AssetsSTC | undefined, assetTypeId: string): ScheduleFeatures {
  const features = stcs?.data.find((stc) => stc.assetTypeId === assetTypeId)?.scheduleFeatures
  if (!features) {
    return DEFAULT_SCHEDULE_FEATURES
  }
  const normalized = withLegacyDefault(features)
  return normalized.supportsTargetType?.length
    ? normalized
    : { ...normalized, supportsTargetType: DEFAULT_SCHEDULE_FEATURES.supportsTargetType }
}
